import { Injectable } from '@angular/core';
import { LoadingController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import {BehaviorSubject} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class LoadingService {
  loading: any;
  isShowingLoader = false;
  widgetCloseButton: HTMLElement | null = null;

  loadingObserver: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  loadingRequestMap: Map<string, boolean> = new Map<string, boolean>();

  constructor(
    private readonly loadingController: LoadingController,
    private readonly translateService: TranslateService
  ) {}

  async show(text?: string, duration?: number) {
    if (!this.isShowingLoader) {
      this.isShowingLoader = true;
      this.loading = await this.loadingController.create({
        message: text ? this.translateService.instant(text) : undefined,
        duration: duration ? duration : undefined,
        mode: 'ios',
        spinner: 'crescent'
      });
      await this.loading.present();
      this.createWidgetCloseButton();
    }
  }

  /**
   * Dismiss all the pending loaders, if any
   */
  async dismiss() {
    if (this.isShowingLoader) {
      await this.loadingController.dismiss();
      this.loading = null;
      this.isShowingLoader = false;
      this.removeWidgetCloseButton();
    }
  }

  private createWidgetCloseButton() {
    // Check if should show button (iframe + mobile)
    const isInIframe = window.self !== window.top;
    const isMobile = window.innerWidth <= 480;
    
    if (!isInIframe || !isMobile) {
      return;
    }

    // Remove existing button if any
    this.removeWidgetCloseButton();

    // Create button element
    const button = document.createElement('button');
    button.className = 'loading-widget-close-button';
    button.setAttribute('type', 'button');
    button.setAttribute('aria-label', 'Voltar para página do parceiro');
    button.setAttribute('title', 'Voltar para página do parceiro');
    button.innerHTML = `
      <svg viewBox="0 0 24 24">
        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
      </svg>
    `;
    
    // Add click handler
    button.addEventListener('click', () => {
      this.closeWidget();
    });

    // Add styles
    this.injectButtonStyles();
    
    // Append to body
    document.body.appendChild(button);
    this.widgetCloseButton = button;
  }

  private removeWidgetCloseButton() {
    if (this.widgetCloseButton) {
      this.widgetCloseButton.remove();
      this.widgetCloseButton = null;
    }
  }

  private closeWidget() {
    // Send message to parent window to close the widget
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({
        type: 'chat:close'
      }, '*');
    }
  }

  private injectButtonStyles() {
    // Check if styles already injected
    if (document.getElementById('loading-widget-close-button-styles')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'loading-widget-close-button-styles';
    style.textContent = `
      .loading-widget-close-button {
        position: fixed;
        top: 13px;
        left: 4px;
        right: auto;
        min-width: 60px;
        height: 40px;
        padding: 0 16px;
        border-radius: 20px;
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%);
        color: #142EBC;
        border: 2px solid rgba(255, 255, 255, 0.3);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10001;
        font-size: 14px;
        font-weight: 600;
        font-family: 'Montserrat', Arial, sans-serif;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        outline: none;
      }

      .loading-widget-close-button:hover {
        background: linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0.95) 100%);
        color: #327ADB;
        transform: scale(1.05);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2), 0 4px 12px rgba(0, 0, 0, 0.15);
      }

      .loading-widget-close-button:active {
        transform: scale(0.98);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      }

      .loading-widget-close-button svg {
        width: 20px;
        height: 20px;
        fill: currentColor;
      }
    `;
    document.head.appendChild(style);
  }

  setLoading(loading: boolean, url: string): void {
    if (!url) {
      throw new Error('The request URL must be provided');
    }
    if (loading === true) {
      this.loadingRequestMap.set(url, loading);
      this.loadingObserver.next(true);
    } else if (loading === false && this.loadingRequestMap.has(url)) {
      this.loadingRequestMap.delete(url);
    }
    if (this.loadingRequestMap.size === 0) {
      this.loadingObserver.next(false);
    }
  }
}
